import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { getFormSubmissions, updateFormStatus } from '../../services/formService';

const statusColors = { new: 'error', read: 'warning', replied: 'success', archived: 'default' };
const typeLabels = {
  contact: 'İletişim',
  demo_request: 'Demo',
  feedback: 'Geri Bildirim',
  partnership: 'Ortaklık',
};

const FormSubmissionManagement = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const result = await getFormSubmissions(params);
      setSubmissions(result.data);
    } catch {
      setSnackbar({ open: true, message: 'Formlar yüklenemedi', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const handleStatus = async (id, status) => {
    try {
      await updateFormStatus(id, status);
      fetchData();
      setSelected(null);
      setSnackbar({ open: true, message: 'Durum güncellendi', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Güncelleme başarısız', severity: 'error' });
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>Form Gönderimleri</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Durum</InputLabel>
          <Select value={statusFilter} label="Durum" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="new">Yeni</MenuItem>
            <MenuItem value="read">Okundu</MenuItem>
            <MenuItem value="replied">Yanıtlandı</MenuItem>
            <MenuItem value="archived">Arşiv</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Tür</InputLabel>
          <Select value={typeFilter} label="Tür" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="contact">İletişim</MenuItem>
            <MenuItem value="demo_request">Demo</MenuItem>
            <MenuItem value="feedback">Geri Bildirim</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tarih</TableCell>
              <TableCell>Ad</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Tür</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">Detay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.map((s) => (
              <TableRow key={s._id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(s)}>
                <TableCell>{new Date(s.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{typeLabels[s.type] || s.type}</TableCell>
                <TableCell>
                  <Chip size="small" label={s.status} color={statusColors[s.status] || 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={(e) => { e.stopPropagation(); setSelected(s); }}>Gör</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.name} — {typeLabels[selected.type]}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>{selected.email}</Typography>
              {selected.company && <Typography variant="body2" gutterBottom>Şirket: {selected.company}</Typography>}
              {selected.phone && <Typography variant="body2" gutterBottom>Tel: {selected.phone}</Typography>}
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <Typography variant="body2" gutterBottom>
                  Ek: {JSON.stringify(selected.metadata)}
                </Typography>
              )}
              <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{selected.message}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleStatus(selected._id, 'read')}>Okundu</Button>
              <Button onClick={() => handleStatus(selected._id, 'replied')} color="success">Yanıtlandı</Button>
              <Button onClick={() => handleStatus(selected._id, 'archived')}>Arşivle</Button>
              <Button onClick={() => setSelected(null)}>Kapat</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FormSubmissionManagement;
